import React, { useState } from "react";
import { Package, Phone, MessageCircle, CheckCircle2, MapPin, Calendar, Weight, Box } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { businessInfo } from "../data/mock";
import { toast } from "sonner";
import { createPickupRequest } from "../lib/db";

const parcelTypes = [
  "Documents",
  "Small Parcel (up to 1 kg)",
  "Medium Parcel (1-5 kg)",
  "Large Parcel (5-20 kg)",
  "Heavy Cargo (20+ kg)",
  "Fragile Items",
  "Commercial Shipment",
  "Other",
];

export default function BookPickupPage() {
  const [formData, setFormData] = useState({
    customer_name: "",
    mobile: "",
    pickup_address: "",
    city: "",
    pickup_date: "",
    parcel_type: "",
    approx_weight: "",
    remarks: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const buildWhatsAppMessage = () => {
    return encodeURIComponent(
      `New Pickup Request - Om Sai Express\n\n` +
      `Name: ${formData.customer_name}\n` +
      `Mobile: ${formData.mobile}\n` +
      `Address: ${formData.pickup_address}\n` +
      `City: ${formData.city}\n` +
      `Pickup Date: ${formData.pickup_date}\n` +
      `Parcel: ${formData.parcel_type}\n` +
      `Weight: ${formData.approx_weight}\n` +
      (formData.remarks ? `Remarks: ${formData.remarks}` : "")
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_name || !formData.mobile || !formData.pickup_address || !formData.city || !formData.pickup_date) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      await createPickupRequest(formData);
      setSubmitted(true);
      toast.success("Pickup request submitted successfully!");

      // Auto-send WhatsApp notification to admin
      const whatsappUrl = `https://wa.me/919873849129?text=${buildWhatsAppMessage()}`;
      window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error("Failed to submit pickup request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Page Header */}
      <section className="bg-brand-dark py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold text-brand-light uppercase tracking-wider mb-2">Schedule a Pickup</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: "'Lora', serif" }}>Book a Pickup</h1>
          <p className="text-white/70 mt-3 max-w-2xl text-base">Fill in the form below and our team will arrange a doorstep pickup for your shipment.</p>
        </div>
      </section>

      {/* Form Section */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border shadow-lg">
            <div className="h-1.5 bg-brand w-full rounded-t-xl" />
            <CardContent className="p-6 md:p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Lora', serif" }}>Pickup Request Submitted!</h3>
                  <p className="text-gray-500 mb-2">Thank you! Our team will contact you shortly to confirm the pickup.</p>
                  <p className="text-sm text-gray-400 mb-6">A WhatsApp notification has been sent to our team.</p>
                  <div className="flex flex-wrap justify-center gap-3">
                    <Button
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({ customer_name: "", mobile: "", pickup_address: "", city: "", pickup_date: "", parcel_type: "", approx_weight: "", remarks: "" });
                      }}
                      variant="outline"
                      className="border-brand text-brand hover:bg-brand hover:text-white"
                    >
                      Book Another Pickup
                    </Button>
                    <Button asChild className="bg-[#25D366] hover:bg-[#1da851] text-white">
                      <a href={businessInfo.whatsappLink} target="_blank" rel="noopener noreferrer">
                        <MessageCircle className="w-4 h-4" />
                        WhatsApp Us
                      </a>
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-brand-lightest flex items-center justify-center">
                      <Package className="w-5 h-5 text-brand" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Lora', serif" }}>Pickup Request Form</h3>
                      <p className="text-xs text-gray-500">Fill in your details and we'll arrange a doorstep pickup.</p>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="customer_name" className="text-sm font-medium text-gray-700 mb-1.5 block">
                          Your Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="customer_name"
                          name="customer_name"
                          value={formData.customer_name}
                          onChange={handleChange}
                          placeholder="Enter your full name"
                          className="border-gray-300 focus:border-brand focus:ring-brand"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mobile" className="text-sm font-medium text-gray-700 mb-1.5 block">
                          Mobile Number <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="mobile"
                          name="mobile"
                          type="tel"
                          value={formData.mobile}
                          onChange={handleChange}
                          placeholder="Enter your mobile number"
                          className="border-gray-300 focus:border-brand focus:ring-brand"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="pickup_address" className="text-sm font-medium text-gray-700 mb-1.5 block">
                        Pickup Address <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="pickup_address"
                        name="pickup_address"
                        value={formData.pickup_address}
                        onChange={handleChange}
                        placeholder="Enter complete pickup address with landmark"
                        rows={2}
                        className="border-gray-300 focus:border-brand focus:ring-brand resize-none"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="city" className="text-sm font-medium text-gray-700 mb-1.5 block">
                          City <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          placeholder="Enter city name"
                          className="border-gray-300 focus:border-brand focus:ring-brand"
                        />
                      </div>
                      <div>
                        <Label htmlFor="pickup_date" className="text-sm font-medium text-gray-700 mb-1.5 block">
                          Preferred Pickup Date <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="pickup_date"
                          name="pickup_date"
                          type="date"
                          value={formData.pickup_date}
                          onChange={handleChange}
                          min={new Date().toISOString().split("T")[0]}
                          className="border-gray-300 focus:border-brand focus:ring-brand"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1.5 block">Parcel Type</Label>
                        <Select value={formData.parcel_type} onValueChange={(v) => setFormData({ ...formData, parcel_type: v })}>
                          <SelectTrigger className="border-gray-300">
                            <SelectValue placeholder="Select parcel type" />
                          </SelectTrigger>
                          <SelectContent>
                            {parcelTypes.map((type) => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="approx_weight" className="text-sm font-medium text-gray-700 mb-1.5 block">Approx. Weight</Label>
                        <Input
                          id="approx_weight"
                          name="approx_weight"
                          value={formData.approx_weight}
                          onChange={handleChange}
                          placeholder="e.g. 2 kg"
                          className="border-gray-300 focus:border-brand focus:ring-brand"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="remarks" className="text-sm font-medium text-gray-700 mb-1.5 block">Remarks (Optional)</Label>
                      <Textarea
                        id="remarks"
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleChange}
                        placeholder="Any special instructions or details..."
                        rows={2}
                        className="border-gray-300 focus:border-brand focus:ring-brand resize-none"
                      />
                    </div>

                    <Separator />

                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-brand hover:bg-brand-dark text-white font-semibold w-full h-12 text-base shadow-md"
                      size="lg"
                    >
                      <Package className="w-5 h-5" />
                      {loading ? "Submitting..." : "Submit Pickup Request"}
                    </Button>

                    <p className="text-xs text-gray-400 text-center">
                      By submitting, a WhatsApp notification will be sent to our team for instant processing.
                    </p>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Quick Contact */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-lg font-bold text-gray-900 mb-3" style={{ fontFamily: "'Lora', serif" }}>Prefer to Call or WhatsApp?</h3>
          <p className="text-gray-500 text-sm mb-5">You can also book a pickup by calling or messaging us directly.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild className="bg-brand hover:bg-brand-dark text-white font-semibold">
              <a href={`tel:${businessInfo.phone}`}>
                <Phone className="w-4 h-4" />
                Call {businessInfo.phone}
              </a>
            </Button>
            <Button asChild className="bg-[#25D366] hover:bg-[#1da851] text-white font-semibold">
              <a href={businessInfo.whatsappLink} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4" />
                WhatsApp Us
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
